import { useEffect, useState } from "react";
import { Download, Upload } from "lucide-react";
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { toast } from "sonner";
import api, { API, errText } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, KorwilBadge, Progress, SectionTitle, TableShell, Td, Th } from "@/components/Shared";

const PIE_COLORS = ["#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#6b7280", "#a855f7"];

export default function Report() {
  const { korwilFilter, user } = useAuth();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/report/summary", { params: { korwil: korwilFilter || undefined } })
      .then(({ data }) => setD(data))
      .catch(() => {});
  }, [korwilFilter]);

  const exportCsv = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/report/export", {
        params: { korwil: korwilFilter || undefined },
        responseType: "blob",
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `laporan-pyx-${Date.now()}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Laporan diunduh");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };


  return (
    <div data-testid="page-report">
      <SectionTitle
        title="Report"
        subtitle="Rekap operasional & sinkronisasi data pusat (import Excel/CSV)"
        right={
          <div className="flex flex-wrap gap-2">
            {["SUPER_ADMIN", "KORWIL"].includes(user?.role) && (
              <a
                data-testid="btn-import-jadwal"
                href="/jadwal/upload"
                className="flex items-center gap-1.5 rounded border border-slate-700 px-3 py-2 text-xs font-semibold text-slate-200 transition-colors hover:bg-slate-800"
              >
                <Upload className="h-3.5 w-3.5" /> Upload Alokasi
              </a>
            )}
            <button
              data-testid="btn-export-report"
              disabled={busy}
              onClick={exportCsv}
              className="flex items-center gap-1.5 rounded bg-blue-600 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              <Download className="h-3.5 w-3.5" /> Cetak Laporan (CSV)
            </button>
          </div>
        }
      />

      <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {[
          ["Distribusi Status Instalasi", d?.status_instalasi, "chart-status-pie"],
          ["Distribusi Status BAPP", d?.bapp, "chart-bapp-pie"],
        ].map(([title, data, tid]) => (
          <Card key={title} className="p-4">
            <h3 className="mb-3 font-display text-base font-semibold text-white">{title}</h3>
            <div className="h-64 min-h-[240px]" data-testid={tid}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={data || []} dataKey="value" nameKey="name" innerRadius={55} outerRadius={90} paddingAngle={2}>
                    {(data || []).map((_, i) => (
                      <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} stroke="#0b0f17" />
                    ))}
                  </Pie>
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: "#111827", border: "1px solid #374151", borderRadius: 6, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Card>
        ))}
      </div>

      <h3 className="mb-3 font-display text-base font-semibold text-white">
        Rekap per Kabupaten/Kota
      </h3>
      <TableShell testId="report-table" empty={!d?.per_kabupaten?.length}>
        <thead>
          <tr>
            <Th>Kabupaten/Kota</Th>
            <Th>Korwil</Th>
            <Th className="text-right">Total Unit</Th>
            <Th className="text-right">Selesai</Th>
            <Th className="text-right">Tanpa Teknisi</Th>
            <Th className="w-[160px]">Progress</Th>
          </tr>
        </thead>
        <tbody>
          {(d?.per_kabupaten || []).map((r, i) => (
            <tr key={`${r.kabupaten_kota}-${r.korwil}-${i}`} className="border-b border-slate-800/70 hover:bg-slate-800/50">
              <Td className="text-slate-100">{r.kabupaten_kota}</Td>
              <Td><KorwilBadge value={r.korwil} /></Td>
              <Td className="text-right font-mono-data text-xs">{r.total_unit}</Td>
              <Td className="text-right font-mono-data text-xs text-emerald-300">{r.selesai}</Td>
              <Td className="text-right font-mono-data text-xs text-red-300">{r.tanpa_teknisi}</Td>
              <Td>
                <div className="flex items-center gap-2">
                  <Progress value={r.progress} />
                  <span className="font-mono-data text-[11px] text-slate-400">{r.progress}%</span>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </TableShell>
    </div>
  );
}
